const contentConfig = [
  {
    header: `
      <p>The road to green, 2023</p>
      <h1>UK cities leading the EV charge</h1>
      <p>Transport is the biggest contributor to greenhouse gas <a href="https://www.statista.com/topics/6270/transport-emissions-in-the-uk" rel="noopener noreferrer" target="_blank">emissions</a> in the UK. That’s why the switch to electric vehicles is a key target in the government’s <a href="https://www.gov.uk/government/publications/net-zero-strategy" rel="noopener noreferrer" target="_blank">net-zero strategies</a>, making up 23% of the total reduction in emissions expected by 2050.</p>
      <p>The sale of new petrol and diesel cars is set to <a href="/sell-my-car/guides/2030-electric-car-switchover">end by 2030</a>. But how are we tracking the electric vehicle (EV) transition? We combined a variety of key metrics to uncover an EV-readiness index that reveals which UK cities and towns are leading the way.</p>
    `,
    id: 'BestEvCities',
    main: `
      <div class="header" style="display: none;">
        <div class="hamburger"></div>
        <div class="lazy"></div>
        <div class="nav">
          <div class="nav-link"></div>
        </div>
      </div>
      <section class="section section-padding bg-white pb-0">
      <div class="container">
        <div class="col-12 col-desk-10 offset-desk-1 col-hd-8 offset-hd-2">
          <div class="toc">
            <div class="toc-title">
              <img loading="lazy" src="https://motorway-ev-index.vercel.app/images/icon-toc.png" alt="Table of contents" />
              <h2>Table of contents</h2>
            </div>
            <ul class="list-ul has-dot">
              <li><a href="#economic_upsides_to_the_ev_transition" class="internal-link">Economic upsides to the EV transition</a></li>
              <li><a href="#electric_vehicle_boom_in_the_uk" class="internal-link">Electric vehicle boom in the UK</a></li>
              <li><a href="#how_the_ev_readiness_index_works" class="internal-link">How the EV-readiness index works</a></li>
              <li><a href="#the_uk_cities_and_towns_leading_the_charge_toward_an_ev_future" class="internal-link">The UK cities and towns leading the charge toward an EV future</a></li>
              <li><a href="#the_full_index" class="internal-link">The full index</a></li>
              <li><a href="#thinking_of_selling_your_car_ahead_of_the_2030_ev_switchover" class="internal-link">Thinking of selling your car ahead of the 2030 EV switchover?</a></li>
            </ul>
          </div>
        </div>
      </div>
      </section>
      <section id="economic_upsides_to_the_ev_transition" class="section section-padding bg-white">
      <div class="container">
        <div class="col-12 col-desk-10 offset-desk-1 col-hd-8 offset-hd-2">
          <div class="number number-line-secondary intro-number">01.</div>
          <h2>Economic upsides to the EV transition</h2>
          <p><strong>The environmental agenda has largely driven the transition to EVs, but there are economic benefits too.</strong></p>
          <ul class="list-ul has-dot" style="margin-top: 45px">
            <li>
              <p>
                <strong>Jobs:</strong> the national shift to net-zero will have flow-on benefits through the jobs market. Charging infrastructure firms are <a href="https://uk.indeed.com/Ev-Charging-jobs?vjk=d29747e5b6e5ed7f" target="_blank" rel="noopener noreferrer">hiring</a>, and schemes like the government’s
                <a href="https://assets.publishing.service.gov.uk/government/uploads/system/uploads/attachment_data/file/936567/10_POINT_PLAN_BOOKLET.pdf" target="_blank" rel="noopener noreferrer">investment</a>
                in mass-scale production of EV batteries will create thousands of green jobs.
              </p>
            </li>
            <li>
              <p><strong>Lower transport costs:</strong> compared to a petrol car, drivers can <a href="https://www.nimblefins.co.uk/how-much-save-electric-car" target="_blank" rel="noopener noreferrer">save</a> over £650 a year on fuel.</p>
            </li>
            <li>
              <p><strong>Free parking:</strong> EVs are eligible for free parking <a href="/sell-my-car/guides/electric-car-questions" target="_blank">while charging</a> in many municipalities.</p>
            </li>
            <li>
              <p><strong>Tax savings:</strong> until 2025, zero-emission <a href="/sell-my-car/guides/do-electric-cars-pay-road-tax" target="_blank">electric cars are exempt from paying vehicle road tax</a>. After that point, their tax rate will depend on their retail price and emissions level.</p>
            </li>
            <li>
              <p>
                <strong>Avoid congestion charge:</strong> electric vehicles usually don’t pay congestion fees, though this is
                <a href="/sell-my-car/guides/do-electric-cars-pay-congestion-charge" target="_blank">subject to change</a>
                as EVs are becoming more common.
              </p>
            </li>
            <li>
              <p>
                <strong>Cheaper long-term:</strong> EVs can cost
                <a href="/sell-my-car/guides/electric-car-questions" target="_blank">40% less than a traditional car</a>
                over their life, even though they’re normally more expensive to buy.
              </p>
            </li>
            <li>
              <p><strong>Good resale value:</strong> interest in EVs is high, so their value is holding up well and is expected to stay steady as the age of 100% EVs approaches. Of course, resale depends on many factors. See this <a href="/sell-my-car/guides/electric-car-depreciation" target="_blank">EV depreciation guide</a> for trends on many popular models.</p>
            </li>
          </ul>
        </div>
      </div>
      </section>
      <section id="electric_vehicle_boom_in_the_uk" class="section section-padding pt-0-touch bg-white">
      <div class="container">
        <div class="col-12 col-desk-10 offset-desk-1 col-hd-8 offset-hd-2">
          <div class="number number-line-secondary intro-number">02.</div>
          <h2>Electric vehicle boom in the UK</h2>
          <p>
            <strong>Before we dive into which cities are making the most progress in the EV transition, let’s first look at the latest EV trends in the UK.</strong>
          </p>
          <p>We’ve focused on 100% battery electric vehicles (BEV) and excluded hybrids as they will be phased out in the transition to zero-emission transport.</p>
          <div class="stats-wrapper">
            <div class="stat">
              <div class="stat-icon">
                <img loading="lazy" src="https://motorway-ev-index.vercel.app/images/icon-stat-sales.png" alt="2021 BEV sales" />
              </div>
              <div class="stat-details">
                <div class="stat-number">190K</div>
                <div class="stat-info">
                  <p>
                    <strong>2021 BEV sales</strong>
                    <span style="display: inline-block; width: 16px; vertical-align: middle; height: 16px; margin-top: -7px" data-toggle="tooltip" data-placement="top" title="More than the previous five years combined.">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd" />
                      </svg>
                    </span>
                  </p>
                </div>
              </div>
            </div>
            <div class="stat">
              <div class="stat-icon">
                <img loading="lazy" src="https://motorway-ev-index.vercel.app/images/icon-stat-calendar.png" alt="Year-on-year BEV increase 2020-2021" />
              </div>
              <div class="stat-details">
                <div class="stat-number">92%</div>
                <div class="stat-info">
                  <p><strong>Year-on-year BEV increase 2020-2021</strong></p>
                </div>
              </div>
            </div>
            <div class="stat">
              <div class="stat-icon">
                <img loading="lazy" src="https://motorway-ev-index.vercel.app/images/icon-stat-cars.png" alt="Number of BEVs on the road" />
              </div>
              <div class="stat-details">
                <div class="stat-number">570K</div>
                <div class="stat-info">
                  <p><strong>Number of BEVs on the road</strong></p>
                </div>
              </div>
            </div>
            <div class="stat">
              <div class="stat-icon">
                <img loading="lazy" src="https://motorway-ev-index.vercel.app/images/icon-stat-register.png" alt="BEVs as a % of new car registrations" />
              </div>
              <div class="stat-details">
                <div class="stat-number">17%</div>
                <div class="stat-info">
                  <p><strong>BEVs as a % of new car registrations</strong></p>
                </div>
              </div>
            </div>
          </div>
          <p class="caption">Source: <a href="https://www.smmt.co.uk/vehicle-data/evs-and-afvs-registrations/" target="_blank" rel="noopener noreferrer">SMMT</a> data to September 2022</p>
        </div>
      </div>
      </section>
      <section id="how_the_ev_readiness_index_works" class="section section-padding pt-0-touch bg-white">
      <div class="container">
        <div class="col-12 col-desk-10 offset-desk-1 col-hd-8 offset-hd-2">
          <div class="number number-line-secondary intro-number">03.</div>
          <h2>How the EV-readiness index works</h2>
          <p>
            <strong>We analysed a range of key metrics to rank UK cities and towns in their progress toward an electric vehicle future.</strong>
          </p>
          <div class="cards-wrapper has-3-cards">
            <div class="card">
              <div class="card-icon">
                <img loading="lazy" src="https://motorway-ev-index.vercel.app/images/icon-sales.png" alt="BEV sales" />
              </div>
              <div class="card-info">
                <p><strong>BEV sales</strong></p>
                <p>New and used BEV sales data trends by location</p>
              </div>
            </div>
            <div class="card">
              <div class="card-icon">
                <img loading="lazy" src="https://motorway-ev-index.vercel.app/images/icon-plug.png" alt="Charger availability" />
              </div>
              <div class="card-info">
                <p><strong>Charger availability</strong></p>
                <p>Number of chargers available per person</p>
              </div>
            </div>
            <div class="card">
              <div class="card-icon">
                <img loading="lazy" src="https://motorway-ev-index.vercel.app/images/icon-air.png" alt="Clean air policy" />
              </div>
              <div class="card-info">
                <p><strong>Clean air policy</strong></p>
                <p>Have or plan to introduce fossil fuel disincentive</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      </section>
      <section id="the_uk_cities_and_towns_leading_the_charge_toward_an_ev_future" class="section section-padding pt-0-touch bg-white">
      <div class="container">
        <div class="col-12 col-desk-10 offset-desk-1 col-hd-8 offset-hd-2">
          <div class="number number-line-secondary intro-number">04.</div>
          <h2>The UK cities and towns leading the charge toward an EV future</h2>
          <p><strong>Let’s take a look at the top 10 for 2022.</strong></p>
          <div class="list-item">
            <span class="list-number">01</span>
            <h3>London</h3>
          </div>
          <p><strong>London has been named as the city leading the charge to an electric vehicle future in the UK</strong></p>
          <p>
            Our capital claimed the highest EV-readiness ranking out of all UK cities. The switch to electric is well underway in London, with flourishing new and used EV sales, and the percentage of BEVs amongst new vehicle registrations is nudging
            <a href="https://www.standard.co.uk/news/london/electric-car-sales-numbers-london-b1010018.html" target="_blank" rel="noopener noreferrer">25%</a>.
          </p>
          <p>
            London is also
            <a href="https://www.londoncouncils.gov.uk/our-key-themes/transport/electric-vehicle-charging/electric-vehicle-charge-points-london" target="_blank" rel="noopener noreferrer">leading</a>
            the way in charging infrastructure with 8,600 public charge points for its 9 million residents. At the end of 2021, this represented a third of the UK’s total charge points and an 85% increase since
            <a href="https://www.london.gov.uk/press-releases/mayoral/ulez-launches-in-central-london" target="_blank" rel="noopener noreferrer">2019</a>.
          </p>
          <p>
            Clean air has been a priority for London’s policymakers since 2008 when they launched a low-emission zone. It was upgraded to the world’s first 24-hour ultra-low emission zone in 2019, and it’s poised to expand again in
            <a href="https://tfl.gov.uk/modes/driving/ultra-low-emission-zone/proposed-ulez-expansion-for-2023" target="_blank" rel="noopener noreferrer">2023</a>.
          </p>
          <figure class="mt-3">
            <img loading="lazy" class="img-responsive lazy is-rounded" data-src="https://motorway-ev-index.vercel.app/images/ev_london.jpeg" alt="London" />
          </figure>
          <p class="caption">
            Credit:
            <a href="https://flickr.com/photos/citytransportinfo/32793536747/in/photolist-RXRrXZ-2d6gCHp-21BssMr-2h2gg1u-2h2ggi8-2gue4dw-2h2ggaH-BxicZL-CsvqUy-2n9FSY9-2ewAGAh-CsvpYA-2fJmRPn-2i75tfJ" target="_blank" rel="noopener noreferrer">Flickr</a>
          </p>
          <div class="list-item">
            <span class="list-number">02</span>
            <h3>Nottingham</h3>
          </div>
          <p><strong>Nottingham achieves second place in our list of cities most prepared for the EV switch</strong></p>
          <p>
            Nottingham is well-advanced in their transition to zero-carbon transport, with solid results across the metrics of the EV-readiness index. EV sales are climbing steadily, and with the UK’s second most
            <a href="https://www.gov.uk/guidance/find-and-use-data-on-public-electric-vehicle-chargepoints" target="_blank" rel="noopener noreferrer">charging stations</a>
            outside of the capital, Nottingham’s EV drivers can easily recharge while out and about.
          </p>
          <p>
            Lack of trust in EV driving range and accessibility to charging points are key
            <a href="https://industryeurope.com/sectors/transportation/new-survey-reveals-obstacles-to-ev-adoption-in-the-uk/" target="_blank" rel="noopener noreferrer">barriers</a> to EV adoption, and Nottingham is addressing those concerns head-on.
          </p>
          <p>
            They are also driving the switch to EVs with innovative
            <a href="https://www.transportnottingham.com/driving/electric-vehicle-projects/" target="_blank" rel="noopener noreferrer">schemes</a> like wireless charging of electric taxis and on-street EV charging.
          </p>
          <figure class="mt-3">
            <img loading="lazy" class="img-responsive lazy is-rounded" data-src="https://motorway-ev-index.vercel.app/images/ev_nottingham.jpeg" alt="Nottingham" />
          </figure>
          <p class="caption">Credit: <a href="https://www.transportnottingham.com/" target="_blank" rel="noopener noreferrer">https://www.transportnottingham.com/</a></p>
          <div class="list-item">
            <span class="list-number">03</span>
            <h3>Bedford</h3>
          </div>
          <p><strong>Bedford scores a podium finish in the race to EV-readiness</strong></p>
          <p>
            Bedford has been a leader in climate action since it
            <a href="https://bbcdevwebfiles.blob.core.windows.net/webfiles/Environmental%20Issues/Sustainability/Strategies/Carbon%20Reduction%20Delivery%20Strategy.pdf" target="_blank" rel="noopener noreferrer">declared</a>
            a climate emergency in 2019. Now, it takes third place in our EV-readiness challenge.
          </p>
          <p>
            Bedford Borough Council has developed an extensive
            <a href="https://bbcdevwebfiles.blob.core.windows.net/webfiles/Environmental%20Issues/Sustainability/Strategies/Carbon%20Reduction%20Delivery%20Strategy.pdf" target="_blank" rel="noopener noreferrer">Net-Zero Strategy</a>
            making installing charge points a key focus. Their innovations include a
            <a href="https://www.bedford.gov.uk/parking-roads-and-travel/parking-bedford-borough/electric-vehicle-chargepoints" target="_blank" rel="noopener noreferrer">policy</a>
            requiring developers to install EV infrastructure in new homes, businesses and community facilities.
          </p>
          <p>
            Electric vehicle sales exhibit strong growth, and many education campaigns are in place to support the switch. For example, the local council offers online EV
            <a href="https://www.bedford.gov.uk/environmental-issues/sustainability/energy-and-water-advice-businesses" target="_blank" rel="noopener noreferrer">webinars</a>
            to demonstrate EV options and advantages to Bedfordshire businesses.
          </p>
          <figure class="mt-3">
            <img loading="lazy" class="img-responsive lazy is-rounded" data-src="https://motorway-ev-index.vercel.app/images/ev_bedford.jpg" alt="Bedford" />
          </figure>
          <p class="caption">Credit: <a href="https://www.jct600.co.uk/electric/" target="_blank" rel="noopener noreferrer">www.jct600.co.uk/electric/</a></p>
          <div class="list-item">
            <span class="list-number">04</span>
            <h3>Manchester</h3>
          </div>
          <p><strong>Manchester was named 4th in the list of cities most prepared for an electric vehicle future</strong></p>
          <p>In Manchester, the switch to electric vehicles is gathering pace with strong sales in both new and used BEVs.</p>
          <p>
            Government
            <a href="https://www.gov.uk/guidance/find-and-use-data-on-public-electric-vehicle-chargepoints" target="_blank" rel="noopener noreferrer">data</a> indicates EV charging infrastructure installation is progressing well, with the number of EV charge points available for Mancunians among the best in the UK.
          </p>
          <p>
            Manchester was to have a clean air zone in place by May 2022, but it’s now
            <a href="https://cleanairgm.com/" target="_blank" rel="noopener noreferrer">under review</a>. Government feedback is required before the new investment-led Greater Manchester clean air plan can be implemented.
          </p>
          <figure class="mt-3">
            <img loading="lazy" class="img-responsive lazy is-rounded" data-src="https://motorway-ev-index.vercel.app/images/ev_manchester.jpeg" alt="Manchester" />
          </figure>
          <p class="caption">Credit: <a href="https://www.jct600.co.uk/electric/" target="_blank" rel="noopener noreferrer">www.jct600.co.uk/electric/</a></p>
          <div class="list-item">
            <span class="list-number">05</span>
            <h3>Newcastle upon Tyne</h3>
          </div>
          <p><strong>Newcastle claims 5th place in the race to an EV future</strong></p>
          <p>Newcastle is rapidly making the switch to electric vehicles with impressive results across the three metrics of our EV-readiness index.</p>
          <p>
            Newcastle upon Tyne recently
            <a href="https://automotiveindustrydigest.com/latest-fleet-news/electric-car-sales-surge-tenfold-since-covid-pandemic-as-newcastle-is-crowned-uks-ev-hotspot/auto-news?utm_source=rss&utm_medium=rss&utm_campaign=electric-car-sales-surge-tenfold-since-covid-pandemic-as-newcastle-is-crowned-uks-ev-hotspot" target="_blank" rel="noopener noreferrer">named</a>
            an EV hotspot, as the UK city with the most electric cars financed in 2022.
          </p>
          <p>
            Readily accessible charging infrastructure is critical to give motorists confidence to switch to EVs.
            <a href="https://www.gov.uk/guidance/find-and-use-data-on-public-electric-vehicle-chargepoints" target="_blank" rel="noopener noreferrer">Data</a> shows there are less than 900 people per charger in Newcastle upon Tyne, which puts charger availability among the best in the UK.
          </p>
          <p>A clean air zone has been <a href="https://www.breathe-cleanair.com/" target="_blank" rel="noopener noreferrer">established</a> in parts of Newcastle. It comes into place in 2023, and will only apply to buses, coaches, taxis, vans and heavy goods vehicles that do not meet national Clean Air Zone emissions standards. Private cars will be exempt from the associated charges.</p>
          <figure class="mt-3">
            <img loading="lazy" class="img-responsive lazy is-rounded" data-src="https://motorway-ev-index.vercel.app/images/ev_newcastle.png" alt="Newcastle upon Tyne" />
          </figure>
          <p class="caption">Credit: <a href="https://www.flash-electrical.co.uk/" target="_blank" rel="noopener noreferrer">https://www.flash-electrical.co.uk/</a></p>
          <div class="list-item">
            <span class="list-number">06</span>
            <h3>Milton Keynes</h3>
          </div>
          <p><strong>Milton Keynes earns 6th place in a list of the UK’s top EV-ready cites</strong></p>
          <p>
            In 2014, Milton Keynes was awarded £9 million in
            <a href="https://researchbriefings.files.parliament.uk/documents/CBP-7480/CBP-7480.pdf" target="_blank" rel="noopener noreferrer">funding</a> in the Go Ultra Low Cities scheme. This boost to creating EV charging infrastructure has enabled Milton Keynes to become one of the UK’s best places for EV charging.
          </p>
          <p>
            They also used the funding to educate residents, with the UK’s first all-EV, multi-brand car
            <a href="https://evexperiencecentre.co.uk/" target="_blank" rel="noopener noreferrer">showroom</a> opening in 2017. Now closed, it was an experience centre aimed at educating motorists on the benefits of EVs to help them confidently make the switch.
          </p>
          <p>
            Milton Keynes also has a
            <a href="https://getaroundmk.org.uk/get-connected/go-electric/electric-vehicles/free-ev-parking" target="_blank" rel="noopener noreferrer">parking</a> scheme to encourage EV uptake with dedicated free parking for ultra-low emission cars.
          </p>
          <figure class="mt-3">
            <img loading="lazy" class="img-responsive lazy is-rounded" data-src="https://motorway-ev-index.vercel.app/images/ev_milton_keynes.png" alt="Milton Keynes" />
          </figure>
          <p class="caption">Credit: <a href="https://www.autocar.co.uk/" target="_blank" rel="noopener noreferrer">https://www.autocar.co.uk/</a></p>
          <div class="list-item">
            <span class="list-number">07</span>
            <h3>Bristol</h3>
          </div>
          <p><strong>Bristol named 7th most EV-ready city in the UK</strong></p>
          <p>The EV revolution is taking off in Bristol, with more and more drivers making the switch.</p>
          <p>
            There are over
            <a href="https://www.gov.uk/guidance/find-and-use-data-on-public-electric-vehicle-chargepoints" target="_blank" rel="noopener noreferrer">200</a> charging points available in Bristol in car parks, shopping centres and parks. In 2023, <a href="https://www.bristol.gov.uk/residents/streets-travel/charge-an-electric-vehicle" target="_blank" rel="noopener noreferrer">150</a> further public charging points will be installed in residential areas, and
            <a href="https://www.gov.uk/government/collections/government-grants-for-low-emission-vehicles#electric-vehicle-homecharge-scheme" target="_blank" rel="noopener noreferrer">grants</a>
            are also available for installing charge points at home.
          </p>
          <p>
            To further encourage the switch to EVs, Bristol City Council offers businesses, sole traders and other organisations a
            <a href="https://www.bristol.gov.uk/residents/streets-travel/free-electric-vehicle-trial-for-local-organisations" target="_blank" rel="noopener noreferrer">free 2-month trial</a>
            of electric vans and cars . The launch of this scheme comes ahead of a
            <a href="https://www.bristol.gov.uk/residents/streets-travel/bristols-caz/exemptions" target="_blank" rel="noopener noreferrer">clean air zone</a> launching in November 2022.
          </p>
          <figure class="mt-3">
            <img loading="lazy" class="img-responsive lazy is-rounded" data-src="https://motorway-ev-index.vercel.app/images/ev_bristol.png" alt="Bristol" />
          </figure>
          <p class="caption">Credit: <a href="https://travelwest.info/" target="_blank" rel="noopener noreferrer">https://travelwest.info/</a></p>
          <div class="list-item">
            <span class="list-number">08</span>
            <h3>Coventry</h3>
          </div>
          <p><strong>Coventry clinches 8th place in the UK’s EV revolution</strong></p>
          <p>
            Coventry is a leading EV-friendly hotspot, with one of the highest charger availability scores in the UK. They have taken advantage of government
            <a href="https://www.coventry.gov.uk/news/article/4074/council-awarded-700000-for-on-street-charging-points" target="_blank" rel="noopener noreferrer">funding</a>
            and have created innovative schemes like
            <a href="https://www.coventry.gov.uk/roads-highways-pavements/electric-vehicle-charging/7" target="_blank" rel="noopener noreferrer">Plug-in Coventry</a> which is one of the UK’s only truly open, fast-charging networks.
          </p>
          <p>
            The electric vehicle revolution is at the heart of Coventry’s economic future. It’s home to the UK’s
            <a href="https://www.ukbic.co.uk/" target="_blank" rel="noopener noreferrer">battery industrialisation centre</a>, and there is also a new gigafactory in the pipeline. The gigafactory will help drive the world’s transition to sustainable transport by producing advanced lithium-ion batteries and creating up to 6,000 new jobs.
          </p>
          <figure class="mt-3">
            <img loading="lazy" class="img-responsive lazy is-rounded" data-src="https://motorway-ev-index.vercel.app/images/ev_coventry.jpg" alt="Coventry" />
          </figure>
          <p class="caption">Credit: <a href="https://www.jct600.co.uk/electric/" target="_blank" rel="noopener noreferrer">www.jct600.co.uk/electric/</a></p>
          <div class="list-item">
            <span class="list-number">09</span>
            <h3>Southampton</h3>
          </div>
          <p><strong>Southampton scores 9th place among the UK cities paving the way for more EV takeup</strong></p>
          <p>
            Southampton’s motorists are embracing the switch to EVs in record numbers. The south coast city recently
            <a href="https://automotiveindustrydigest.com/latest-fleet-news/electric-car-sales-surge-tenfold-since-covid-pandemic-as-newcastle-is-crowned-uks-ev-hotspot/auto-news?utm_source=rss&utm_medium=rss&utm_campaign=electric-car-sales-surge-tenfold-since-covid-pandemic-as-newcastle-is-crowned-uks-ev-hotspot" target="_blank rel="noopener noreferrer">ranked</a>
            in second place in a list of UK cities with the most EVs financed in 2022.
          </p>
          <p>The city council has developed a comprehensive <a href="http://southampton.gov.uk/GreenCity" target="_blank">green city plan</a> which includes a goal to be in the top 10% of UK cities for public electric charge points by 2025. They offer a range of <a href="https://www.southampton.gov.uk/travel-transport/find-and-plan/electric-vehicles/" target="_blank" rel="noopener noreferrer">incentives</a> to encourage residents and businesses to switch to EVs, including parking and tollway discounts.</p>
          <figure class="mt-3">
            <img loading="lazy" class="img-responsive lazy is-rounded" data-src="https://motorway-ev-index.vercel.app/images/ev_southampton.jpeg" alt="Southampton" />
          </figure>
          <p class="caption">Credit: <a href="https://www.southampton.gov.uk/" target="_blank" rel="noopener noreferrer">https://www.southampton.gov.uk/</a></p>
          <div class="list-item">
            <span class="list-number">10</span>
            <h3>Oxford</h3>
          </div>
          <p><strong>Oxford earns 10th place in the UK cities most prepared for an EV future</strong></p>
          <p>Oxford residents have shown an enthusiastic takeup of EVs, with solid growth evident in new and used EV sales.</p>
          <p>
            Oxford is home to Europe’s most powerful EV charging hub. The recently opened
            <a href="https://energysuperhuboxford.org/about-the-project/" target="_blank" rel="noopener noreferrer">Energy Superhub Oxford</a> offers ultra-rapid charging for 42 vehicles and is 100% powered by renewable energy.
          </p>
          <p>
            A
            <a href="https://www.oxfordshire.gov.uk/residents/roads-and-transport/oxford-zero-emission-zone-zez/about-zero-emission-zone" target="_blank" rel="noopener noreferrer">zero-emission zone</a>
            pilot scheme has recently been established in Oxford to encourage people to switch to fully-electric vehicles. A city-centre trial was launched in February 2022, with plans to expand to a wider zone following further consultation.
          </p>
          <figure class="mt-3">
            <img loading="lazy" class="img-responsive lazy is-rounded" data-src="https://motorway-ev-index.vercel.app/images/ev_oxford.jpeg" alt="Oxford" />
          </figure>
          <p class="caption">Credit: <a href="https://www.pivot-power.co.uk/" target="_blank" rel="noopener noreferrer">https://www.pivot-power.co.uk/</a></p>
          <p><strong>A special mention must go to the remaining towns and cities in the top twenty index.</strong></p>
          <p>Ranking highly in EV-readiness shows a deep commitment to the UK’s transport revolution, and these areas are all making efforts to provide charging points and incentives for fully-electric drivers.</p>
          <ol start="11">
            <li>Brighton</li>
            <li>Solihull</li>
            <li>Leeds</li>
            <li>Warrington</li>
            <li>Banbury</li>
            <li>Derby</li>
            <li>Leicester</li>
            <li>Stafford</li>
            <li>Glasgow</li>
            <li>Barnsley</li>
          </ol>
        </div>
      </div>
      </section>
      <section id="the_full_index" class="section section-padding pt-0-touch bg-white">
      <div class="container">
        <div style="text-align: center; max-width: 800px; margin: auto">
          <h2>The full index</h2>
          <p>By gathering metrics on electric vehicle sales, charger availability and local clean air zones, we have created an index of those towns leading the charge to EV adoption. The full index is presented below.</p>
        </div>
        <table id="motorway_ev_index_table">
          <thead>
            <tr>
              <th>Seller Town</th>
              <th>Electric Vehicle Sales Rank</th>
              <th>Charger availability rank</th>
              <th>People per charger</th>
              <th>Combined EV rank</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>London</td>
              <td>1</td>
              <td>15</td>
              <td>1,672</td>
              <td>1</td>
            </tr>
            <tr>
              <td>Nottingham</td>
              <td>5</td>
              <td>7</td>
              <td>943</td>
              <td>2</td>
            </tr>
            <tr>
              <td>Bedford</td>
              <td>14</td>
              <td>12</td>
              <td>1,389</td>
              <td>3</td>
            </tr>
            <tr>
              <td>Manchester</td>
              <td>7</td>
              <td>13</td>
              <td>1,423</td>
              <td>4</td>
            </tr>
            <tr>
              <td>Newcastle upon Tyne</td>
              <td>26</td>
              <td>6</td>
              <td>863</td>
              <td>5</td>
            </tr>
            <tr>
              <td>Milton Keynes</td>
              <td>19</td>
              <td>5</td>
              <td>810</td>
              <td>6</td>
            </tr>
            <tr>
              <td>Bristol</td>
              <td>2</td>
              <td>51</td>
              <td>3,011</td>
              <td>7</td>
            </tr>
            <tr>
              <td>Oxford</td>
              <td>49</td>
              <td>31</td>
              <td>2,226</td>
              <td>8</td>
            </tr>
            <tr>
              <td>Coventry</td>
              <td>60</td>
              <td>3</td>
              <td>607</td>
              <td>9</td>
            </tr>
            <tr>
              <td>Southampton</td>
              <td>9</td>
              <td>46</td>
              <td>2,798</td>
              <td>10</td>
            </tr>
            <tr>
              <td>Brighton</td>
              <td>76</td>
              <td>2</td>
              <td>521</td>
              <td>11</td>
            </tr>
            <tr>
              <td>Solihull</td>
              <td>20</td>
              <td>39</td>
              <td>2,533</td>
              <td>12</td>
            </tr>
            <tr>
              <td>Leeds</td>
              <td>17</td>
              <td>33</td>
              <td>2,242</td>
              <td>13</td>
            </tr>
            <tr>
              <td>Warrington</td>
              <td>31</td>
              <td>25</td>
              <td>2,094</td>
              <td>14</td>
            </tr>
            <tr>
              <td>Banbury</td>
              <td>36</td>
              <td>18</td>
              <td>1,802</td>
              <td>15</td>
            </tr>
            <tr>
              <td>Derby</td>
              <td>10</td>
              <td>57</td>
              <td>3,220</td>
              <td>16</td>
            </tr>
            <tr>
              <td>Leicester</td>
              <td>19</td>
              <td>70</td>
              <td>3,855</td>
              <td>17</td>
            </tr>
            <tr>
              <td>Stafford</td>
              <td>71</td>
              <td>36</td>
              <td>2,387</td>
              <td>18</td>
            </tr>
            <tr>
              <td>Glasgow</td>
              <td>3</td>
              <td>85</td>
              <td>4,415</td>
              <td>19</td>
            </tr>
            <tr>
              <td>Barnsley</td>
              <td>52</td>
              <td>43</td>
              <td>2,646</td>
              <td>20</td>
            </tr>
            <tr>
              <td>Darlington</td>
              <td>43</td>
              <td>44</td>
              <td>2,717</td>
              <td>21</td>
            </tr>
            <tr>
              <td>Bromley</td>
              <td>83</td>
              <td>22</td>
              <td>1,997</td>
              <td>22</td>
            </tr>
            <tr>
              <td>Stockton-on-Tees</td>
              <td>92</td>
              <td>8</td>
              <td>987</td>
              <td>23</td>
            </tr>
            <tr>
              <td>Loughborough</td>
              <td>52</td>
              <td>29</td>
              <td>2,140</td>
              <td>24</td>
            </tr>
            <tr>
              <td>Birmingham</td>
              <td>6</td>
              <td>121</td>
              <td>7,751</td>
              <td>25</td>
            </tr>
            <tr>
              <td>Chelmsford</td>
              <td>11</td>
              <td>79</td>
              <td>4,130</td>
              <td>26</td>
            </tr>
            <tr>
              <td>Rochdale</td>
              <td>57</td>
              <td>86</td>
              <td>4,434</td>
              <td>27</td>
            </tr>
            <tr>
              <td>Eastleigh</td>
              <td>76</td>
              <td>11</td>
              <td>1,356</td>
              <td>28</td>
            </tr>
            <tr>
              <td>Hayes</td>
              <td>85</td>
              <td>14</td>
              <td>1,667</td>
              <td>29</td>
            </tr>
            <tr>
              <td>St Albans</td>
              <td>25</td>
              <td>87</td>
              <td>4,451</td>
              <td>30</td>
            </tr>
            <tr>
              <td>Northampton</td>
              <td>24</td>
              <td>81</td>
              <td>4,235</td>
              <td>31</td>
            </tr>
            <tr>
              <td>Horsham</td>
              <td>48</td>
              <td>61</td>
              <td>3,431</td>
              <td>32</td>
            </tr>
            <tr>
              <td>Reading</td>
              <td>11</td>
              <td>97</td>
              <td>5,300</td>
              <td>33</td>
            </tr>
            <tr>
              <td>Norwich</td>
              <td>11</td>
              <td>94</td>
              <td>5,075</td>
              <td>34</td>
            </tr>
            <tr>
              <td>Swindon</td>
              <td>18</td>
              <td>78</td>
              <td>4,125</td>
              <td>35</td>
            </tr>
            <tr>
              <td>Wokingham</td>
              <td>42</td>
              <td>77</td>
              <td>4,114</td>
              <td>36</td>
            </tr>
            <tr>
              <td>Aylesbury</td>
              <td>72</td>
              <td>40</td>
              <td>2,578</td>
              <td>37</td>
            </tr>
            <tr>
              <td>Haywards Heath</td>
              <td>81</td>
              <td>10</td>
              <td>1,209</td>
              <td>38</td>
            </tr>
            <tr>
              <td>Wilmslow</td>
              <td>52</td>
              <td>76</td>
              <td>4,069</td>
              <td>39</td>
            </tr>
            <tr>
              <td>Wakefield</td>
              <td>63</td>
              <td>58</td>
              <td>3,291</td>
              <td>40</td>
            </tr>
            <tr>
              <td>Maidstone</td>
              <td>35</td>
              <td>68</td>
              <td>3,711</td>
              <td>41</td>
            </tr>
            <tr>
              <td>Falkirk</td>
              <td>44</td>
              <td>93</td>
              <td>5,044</td>
              <td>42</td>
            </tr>
            <tr>
              <td>Newark</td>
              <td>98</td>
              <td>23</td>
              <td>2,006</td>
              <td>43</td>
            </tr>
            <tr>
              <td>Slough</td>
              <td>75</td>
              <td>83</td>
              <td>4,310</td>
              <td>44</td>
            </tr>
            <tr>
              <td>Peterborough</td>
              <td>55</td>
              <td>82</td>
              <td>4,299</td>
              <td>45</td>
            </tr>
            <tr>
              <td>Chester</td>
              <td>43</td>
              <td>67</td>
              <td>3,621</td>
              <td>46</td>
            </tr>
            <tr>
              <td>Middlesbrough</td>
              <td>38</td>
              <td>88</td>
              <td>4,460</td>
              <td>47</td>
            </tr>
            <tr>
              <td>Hitchin</td>
              <td>33</td>
              <td>106</td>
              <td>6,017</td>
              <td>48</td>
            </tr>
            <tr>
              <td>York</td>
              <td>67</td>
              <td>35</td>
              <td>2,365</td>
              <td>49</td>
            </tr>
            <tr>
              <td>Fareham</td>
              <td>85</td>
              <td>90</td>
              <td>4,783</td>
              <td>50</td>
            </tr>
            <tr>
              <td>Sevenoaks</td>
              <td>89</td>
              <td>20</td>
              <td>1,859</td>
              <td>51</td>
            </tr>
            <tr>
              <td>Gloucester</td>
              <td>56</td>
              <td>73</td>
              <td>3,949</td>
              <td>52</td>
            </tr>
            <tr>
              <td>Andover</td>
              <td>88</td>
              <td>26</td>
              <td>2,114</td>
              <td>53</td>
            </tr>
            <tr>
              <td>Liverpool</td>
              <td>22</td>
              <td>102</td>
              <td>5,878</td>
              <td>54</td>
            </tr>
            <tr>
              <td>Exeter</td>
              <td>75</td>
              <td>49</td>
              <td>2,872</td>
              <td>55</td>
            </tr>
            <tr>
              <td>Ashford</td>
              <td>81</td>
              <td>55</td>
              <td>3,139</td>
              <td>57</td>
            </tr>
            <tr>
              <td>Lincoln</td>
              <td>93</td>
              <td>34</td>
              <td>2,344</td>
              <td>58</td>
            </tr>
            <tr>
              <td>Edinburgh</td>
              <td>13</td>
              <td>124</td>
              <td>8,611</td>
              <td>59</td>
            </tr>
            <tr>
              <td>Oldham</td>
              <td>108</td>
              <td>48</td>
              <td>2,832</td>
              <td>60</td>
            </tr>
            <tr>
              <td>Preston</td>
              <td>27</td>
              <td>114</td>
              <td>6,667</td>
              <td>61</td>
            </tr>
            <tr>
              <td>Stanmore</td>
              <td>101</td>
              <td>74</td>
              <td>3,950</td>
              <td>62</td>
            </tr>
            <tr>
              <td>Worcester</td>
              <td>70</td>
              <td>64</td>
              <td>3,505</td>
              <td>63</td>
            </tr>
            <tr>
              <td>Ormskirk</td>
              <td>139</td>
              <td>9</td>
              <td>1,146</td>
              <td>64</td>
            </tr>
            <tr>
              <td>Rochester</td>
              <td>106</td>
              <td>38</td>
              <td>2,519</td>
              <td>65</td>
            </tr>
            <tr>
              <td>Chesterfield</td>
              <td>99</td>
              <td>16</td>
              <td>1,687</td>
              <td>66</td>
            </tr>
            <tr>
              <td>Plymouth</td>
              <td>102</td>
              <td>45</td>
              <td>2,768</td>
              <td>67</td>
            </tr>
            <tr>
              <td>Crawley</td>
              <td>34</td>
              <td>115</td>
              <td>6,943</td>
              <td>68</td>
            </tr>
            <tr>
              <td>Guildford</td>
              <td>87</td>
              <td>54</td>
              <td>3,125</td>
              <td>69</td>
            </tr>
            <tr>
              <td>Newbury</td>
              <td>147</td>
              <td>4</td>
              <td>806</td>
              <td>70</td>
            </tr>
            <tr>
              <td>Cheltenham</td>
              <td>23</td>
              <td>128</td>
              <td>9,704</td>
              <td>71</td>
            </tr>
            <tr>
              <td>Wellingborough</td>
              <td>50</td>
              <td>100</td>
              <td>5,620</td>
              <td>72</td>
            </tr>
            <tr>
              <td>Leamington Spa</td>
              <td>114</td>
              <td>37</td>
              <td>2,486</td>
              <td>73</td>
            </tr>
            <tr>
              <td>Stockport</td>
              <td>64</td>
              <td>92</td>
              <td>4,966</td>
              <td>74</td>
            </tr>
            <tr>
              <td>Chorley</td>
              <td>120</td>
              <td>27</td>
              <td>2,118</td>
              <td>75</td>
            </tr>
            <tr>
              <td>Keighley</td>
              <td>31</td>
              <td>139</td>
              <td>12,543</td>
              <td>76</td>
            </tr>
            <tr>
              <td>Altrincham</td>
              <td>51</td>
              <td>103</td>
              <td>5,935</td>
              <td>77</td>
            </tr>
            <tr>
              <td>Rugby</td>
              <td>56</td>
              <td>118</td>
              <td>7,315</td>
              <td>78</td>
            </tr>
            <tr>
              <td>Ilford</td>
              <td>45</td>
              <td>99</td>
              <td>5,606</td>
              <td>79</td>
            </tr>
            <tr>
              <td>Watford</td>
              <td>113</td>
              <td>17</td>
              <td>1,746</td>
              <td>80</td>
            </tr>
            <tr>
              <td>Colchester</td>
              <td>56</td>
              <td>91</td>
              <td>4,874</td>
              <td>81</td>
            </tr>
            <tr>
              <td>Widnes</td>
              <td>110</td>
              <td>50</td>
              <td>2,927</td>
              <td>82</td>
            </tr>
            <tr>
              <td>Sheffield</td>
              <td>43</td>
              <td>146</td>
              <td>17,574</td>
              <td>83</td>
            </tr>
            <tr>
              <td>Aberdeen</td>
              <td>97</td>
              <td>71</td>
              <td>3,856</td>
              <td>84</td>
            </tr>
            <tr>
              <td>Maidenhead</td>
              <td>90</td>
              <td>84</td>
              <td>4,322</td>
              <td>85</td>
            </tr>
            <tr>
              <td>Camberley</td>
              <td>118</td>
              <td>69</td>
              <td>3,804</td>
              <td>86</td>
            </tr>
            <tr>
              <td>Croydon</td>
              <td>141</td>
              <td>30</td>
              <td>2,222</td>
              <td>87</td>
            </tr>
            <tr>
              <td>Bolton</td>
              <td>75</td>
              <td>56</td>
              <td>3,141</td>
              <td>88</td>
            </tr>
            <tr>
              <td>Salisbury</td>
              <td>107</td>
              <td>42</td>
              <td>2,632</td>
              <td>89</td>
            </tr>
            <tr>
              <td>Rotherham</td>
              <td>134</td>
              <td>47</td>
              <td>2,800</td>
              <td>90</td>
            </tr>
            <tr>
              <td>Huddersfield</td>
              <td>50</td>
              <td>132</td>
              <td>10,644</td>
              <td>91</td>
            </tr>
            <tr>
              <td>Halifax</td>
              <td>51</td>
              <td>131</td>
              <td>10,328</td>
              <td>92</td>
            </tr>
            <tr>
              <td>Sunderland</td>
              <td>134</td>
              <td>21</td>
              <td>1,874</td>
              <td>93</td>
            </tr>
            <tr>
              <td>Newport</td>
              <td>59</td>
              <td>101</td>
              <td>5,790</td>
              <td>94</td>
            </tr>
            <tr>
              <td>Wolverhampton</td>
              <td>88</td>
              <td>98</td>
              <td>5,379</td>
              <td>95</td>
            </tr>
            <tr>
              <td>Bury</td>
              <td>116</td>
              <td>53</td>
              <td>3,052</td>
              <td>96</td>
            </tr>
            <tr>
              <td>Ipswich</td>
              <td>77</td>
              <td>104</td>
              <td>5,961</td>
              <td>97</td>
            </tr>
            <tr>
              <td>Bracknell</td>
              <td>105</td>
              <td>75</td>
              <td>4,005</td>
              <td>98</td>
            </tr>
            <tr>
              <td>Worksop</td>
              <td>145</td>
              <td>24</td>
              <td>2,060</td>
              <td>99</td>
            </tr>
            <tr>
              <td>Sale</td>
              <td>82</td>
              <td>152</td>
              <td>27,845</td>
              <td>100</td>
            </tr>
            <tr>
              <td>Wrexham</td>
              <td>107</td>
              <td>105</td>
              <td>5,972</td>
              <td>101</td>
            </tr>
            <tr>
              <td>Sutton</td>
              <td>102</td>
              <td>116</td>
              <td>6,948</td>
              <td>102</td>
            </tr>
            <tr>
              <td>Romford</td>
              <td>68</td>
              <td>125</td>
              <td>8,636</td>
              <td>103</td>
            </tr>
            <tr>
              <td>Cambridge</td>
              <td>87</td>
              <td>60</td>
              <td>3,371</td>
              <td>104</td>
            </tr>
            <tr>
              <td>Hereford</td>
              <td>154</td>
              <td>32</td>
              <td>2,238</td>
              <td>105</td>
            </tr>
            <tr>
              <td>Doncaster</td>
              <td>64</td>
              <td>96</td>
              <td>5,271</td>
              <td>106</td>
            </tr>
            <tr>
              <td>Dundee</td>
              <td>120</td>
              <td>41</td>
              <td>2,591</td>
              <td>107</td>
            </tr>
            <tr>
              <td>Harrow</td>
              <td>63</td>
              <td>133</td>
              <td>10,876</td>
              <td>108</td>
            </tr>
            <tr>
              <td>Blackburn</td>
              <td>67</td>
              <td>129</td>
              <td>9,768</td>
              <td>109</td>
            </tr>
            <tr>
              <td>Huntingdon</td>
              <td>35</td>
              <td>147</td>
              <td>17,735</td>
              <td>110</td>
            </tr>
            <tr>
              <td>Hemel Hempstead</td>
              <td>59</td>
              <td>145</td>
              <td>17,126</td>
              <td>111</td>
            </tr>
            <tr>
              <td>Kettering</td>
              <td>133</td>
              <td>52</td>
              <td>3,032</td>
              <td>112</td>
            </tr>
            <tr>
              <td>Enfield</td>
              <td>80</td>
              <td>142</td>
              <td>13,899</td>
              <td>113</td>
            </tr>
            <tr>
              <td>Basingstoke</td>
              <td>139</td>
              <td>59</td>
              <td>3,364</td>
              <td>114</td>
            </tr>
            <tr>
              <td>Bishop&#39;s Stortford</td>
              <td>101</td>
              <td>123</td>
              <td>8,218</td>
              <td>115</td>
            </tr>
            <tr>
              <td>Bradford</td>
              <td>118</td>
              <td>107</td>
              <td>6,236</td>
              <td>116</td>
            </tr>
            <tr>
              <td>Eastbourne</td>
              <td>115</td>
              <td>66</td>
              <td>3,582</td>
              <td>117</td>
            </tr>
            <tr>
              <td>Brentwood</td>
              <td>129</td>
              <td>95</td>
              <td>5,259</td>
              <td>118</td>
            </tr>
            <tr>
              <td>Crewe</td>
              <td>84</td>
              <td>80</td>
              <td>4,198</td>
              <td>119</td>
            </tr>
            <tr>
              <td>Warwick</td>
              <td>119</td>
              <td>65</td>
              <td>3,536</td>
              <td>120</td>
            </tr>
            <tr>
              <td>Pinner</td>
              <td>122</td>
              <td>62</td>
              <td>3,459</td>
              <td>121</td>
            </tr>
            <tr>
              <td>Worthing</td>
              <td>78</td>
              <td>112</td>
              <td>6,607</td>
              <td>122</td>
            </tr>
            <tr>
              <td>Cannock</td>
              <td>110</td>
              <td>117</td>
              <td>7,177</td>
              <td>123</td>
            </tr>
            <tr>
              <td>Sutton Coldfield</td>
              <td>76</td>
              <td>144</td>
              <td>15,290</td>
              <td>124</td>
            </tr>
            <tr>
              <td>Bury Saint Edmunds</td>
              <td>144</td>
              <td>63</td>
              <td>3,502</td>
              <td>125</td>
            </tr>
            <tr>
              <td>Harrogate</td>
              <td>89</td>
              <td>143</td>
              <td>14,843</td>
              <td>126</td>
            </tr>
            <tr>
              <td>Macclesfield</td>
              <td>112</td>
              <td>110</td>
              <td>6,395</td>
              <td>127</td>
            </tr>
            <tr>
              <td>Luton</td>
              <td>131</td>
              <td>89</td>
              <td>4,527</td>
              <td>128</td>
            </tr>
            <tr>
              <td>Woking</td>
              <td>80</td>
              <td>119</td>
              <td>7,424</td>
              <td>129</td>
            </tr>
            <tr>
              <td>Wigan</td>
              <td>121</td>
              <td>134</td>
              <td>10,963</td>
              <td>130</td>
            </tr>
            <tr>
              <td>Telford</td>
              <td>114</td>
              <td>138</td>
              <td>12,332</td>
              <td>131</td>
            </tr>
            <tr>
              <td>Bournemouth</td>
              <td>123</td>
              <td>108</td>
              <td>6,292</td>
              <td>132</td>
            </tr>
            <tr>
              <td>Daventry</td>
              <td>153</td>
              <td>72</td>
              <td>3,941</td>
              <td>133</td>
            </tr>
            <tr>
              <td>Tamworth</td>
              <td>103</td>
              <td>130</td>
              <td>10,246</td>
              <td>134</td>
            </tr>
            <tr>
              <td>High Wycombe</td>
              <td>79</td>
              <td>109</td>
              <td>6,343</td>
              <td>135</td>
            </tr>
            <tr>
              <td>Bexleyheath</td>
              <td>102</td>
              <td>153</td>
              <td>35,214</td>
              <td>136</td>
            </tr>
            <tr>
              <td>Tadley</td>
              <td>121</td>
              <td>136</td>
              <td>11,473</td>
              <td>137</td>
            </tr>
            <tr>
              <td>Cardiff</td>
              <td>86</td>
              <td>120</td>
              <td>7,581</td>
              <td>138</td>
            </tr>
            <tr>
              <td>Wirral</td>
              <td>61</td>
              <td>156</td>
              <td>161,633</td>
              <td>139</td>
            </tr>
            <tr>
              <td>Walsall</td>
              <td>109</td>
              <td>149</td>
              <td>19,127</td>
              <td>140</td>
            </tr>
            <tr>
              <td>Grays</td>
              <td>133</td>
              <td>111</td>
              <td>6,411</td>
              <td>141</td>
            </tr>
            <tr>
              <td>Swansea</td>
              <td>116</td>
              <td>127</td>
              <td>8,834</td>
              <td>142</td>
            </tr>
            <tr>
              <td>Nuneaton</td>
              <td>142</td>
              <td>113</td>
              <td>6,621</td>
              <td>143</td>
            </tr>
            <tr>
              <td>Stoke-on-Trent</td>
              <td>70</td>
              <td>135</td>
              <td>10,964</td>
              <td>144</td>
            </tr>
            <tr>
              <td>Bridgwater</td>
              <td>118</td>
              <td>141</td>
              <td>13,759</td>
              <td>145</td>
            </tr>
            <tr>
              <td>Barnet</td>
              <td>116</td>
              <td>155</td>
              <td>56,548</td>
              <td>146</td>
            </tr>
            <tr>
              <td>Leatherhead</td>
              <td>143</td>
              <td>126</td>
              <td>8,709</td>
              <td>147</td>
            </tr>
            <tr>
              <td>Northwich</td>
              <td>148</td>
              <td>122</td>
              <td>7,904</td>
              <td>148</td>
            </tr>
            <tr>
              <td>Hull</td>
              <td>124</td>
              <td>150</td>
              <td>23,741</td>
              <td>149</td>
            </tr>
            <tr>
              <td>Orpington</td>
              <td>126</td>
              <td>148</td>
              <td>18,063</td>
              <td>150</td>
            </tr>
            <tr>
              <td>Harlow</td>
              <td>135</td>
              <td>137</td>
              <td>11,796</td>
              <td>151</td>
            </tr>
            <tr>
              <td>Southport</td>
              <td>137</td>
              <td>140</td>
              <td>13,100</td>
              <td>152</td>
            </tr>
            <tr>
              <td>Burton-on-Trent</td>
              <td>122</td>
              <td>154</td>
              <td>36,150</td>
              <td>153</td>
            </tr>
            <tr>
              <td>Chatham</td>
              <td>140</td>
              <td>151</td>
              <td>25,170</td>
              <td>154</td>
            </tr>
          </tbody>
        </table>
      </div>
      </section>
      <section id="thinking_of_selling_your_car_ahead_of_the_2030_ev_switchover" class="section section-padding pt-0-touch bg-white">
      <div class="container">
        <div class="col-12 col-desk-10 offset-desk-1 col-hd-8 offset-hd-2">
          <div class="number number-line-secondary intro-number">05.</div>
          <h2>Watch this space</h2>

          <p>With the 2030 deadline fast approaching, it’s encouraging to see these UK cities and towns embracing the electric vehicle revolution. Their residents enthusiastically adopt the technology, and many municipalities offer incentives and education to encourage motorists to make the switch. Congratulations to all the UK cities leading the charge. We’ll be following your progress with interest.</p>

          <h3>Thinking of selling your car ahead of the 2030 EV switchover?</h3>
          <p><a href="/sell-my-car/guides/2030-electric-car-switchover" target="_blank">Read this guide</a></p>
        </div>
      </div>
      </section>
`,
  },
];
export default contentConfig;
